import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { deleteAccount, updatePassword } from "../../../api/profile";
import { VALIDATION_RULES } from "../../../utils/formValidation";
import { Button } from "../../common/Button";
import { DeleteModal } from "../../common/DeleteModal";
import { Modal } from "../../common/Modal";
import { PasswordField } from "../../common/PasswordField";
import { ProfileCard } from "./ProfileCard";

const EMPTY_PASSWORD_FORM = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export const ProfileSecurityTab = ({ accountId }: { accountId: number }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD_FORM);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const isPasswordFormIncomplete =
    !passwordForm.currentPassword ||
    !passwordForm.newPassword ||
    !passwordForm.confirmPassword;

  const handleDeleteAccount = async () => {
    setDeleteError("");
    try {
      await deleteAccount(accountId);
      localStorage.removeItem("accessToken");
      navigate("/account/delete/confirmation");
    } catch (error) {
      console.error("Erreur lors de la suppression du compte:", error);
      setDeleteError(t("account-delete-error"));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t("profile-error-password-match"));
      return;
    }

    if (!VALIDATION_RULES.password().test(passwordForm.newPassword)) {
      setPasswordError(t("profile-error-password-invalid"));
      return;
    }

    try {
      await updatePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      setPasswordForm(EMPTY_PASSWORD_FORM);
      setPasswordSuccess(t("profile-password-updated"));

      setTimeout(() => {
        setIsPasswordModalOpen(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (error: any) {
      setPasswordError(
        error.message === "current_password_incorrect"
          ? t("profile-error-old-password")
          : t("profile-error-update")
      );
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <ProfileCard
        title={t("profile-password-title")}
        subtitle={t("profile-password-subtitle")}
        icon="lock"
      >
        <Button
          type="button"
          variant="secondary"
          className="text-sm"
          onClick={() => setIsPasswordModalOpen(true)}
        >
          {t("profile-password-button")}
        </Button>
      </ProfileCard>

      <ProfileCard
        title={t("profile-delete-title")}
        subtitle={t("profile-delete-subtitle")}
        icon="trash-can"
      >
        <Button
          type="button"
          variant="warning"
          className="text-sm"
          onClick={() => setIsDeleteModalOpen(true)}
        >
          {t("profile-delete-title")}
        </Button>
      </ProfileCard>

      <DeleteModal
        title={t("profile-delete-title")}
        warningText={t("profile-delete-warning")}
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteError("");
        }}
        handleConfirmDelete={handleDeleteAccount}
        error={deleteError}
      />

      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title={t("profile-password-button")}
        maxWidth="sm"
      >
        <form onSubmit={handlePasswordSubmit} className="space-y-6 py-4">
          {passwordError && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="p-4 bg-secondary bg-opacity-20 border border-secondary text-tertiary rounded-lg">
              {passwordSuccess}
            </div>
          )}

          <div className="space-y-4">
            <PasswordField
              label={t("profile-password-current")}
              name="currentPassword"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              required
            />
            <PasswordField
              label={t("profile-password-new")}
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              required
              showRules
            />
            <PasswordField
              label={t("profile-password-confirm")}
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              required
            />
          </div>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsPasswordModalOpen(false)}
            >
              {t("button-cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isPasswordFormIncomplete}
              className={
                isPasswordFormIncomplete ? "opacity-50 cursor-not-allowed" : ""
              }
            >
              {t("button-save")}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
